/**
 * "Report a quality complaint" (kanban 2a28d4c0): a rep or CSR files a
 * complaint straight to the quality desk. The backend writes a structured
 * quality issue and an open thread in the desk's Triage
 * (POST /api/portal/quality-complaints).
 *
 * Customer defaults to the one the page is about; the picker offers the
 * customers on screen and accepts any customer number typed in.
 */
import { useState, type FormEvent } from 'react';
import { trackEvent } from '../../../services/portalTracking';
import { reportQualityComplaint, type ReportComplaintResult } from '../../../services/portalQualityAsks';
import modal from '../AddCustomerModal.module.css';
import styles from './Complaints.module.css';

export interface ComplaintCustomerOption {
  customer_no: string;
  name: string | null;
}

interface ReportComplaintModalProps {
  defaultCustomerNo?: string;
  customers: ComplaintCustomerOption[];
  surface: 'customers' | 'customer_detail';
  onClose: () => void;
  onFiled?: (result: ReportComplaintResult) => void;
}

export default function ReportComplaintModal({
  defaultCustomerNo = '',
  customers,
  surface,
  onClose,
  onFiled,
}: ReportComplaintModalProps) {
  const [customerNo, setCustomerNo] = useState(defaultCustomerNo);
  const [product, setProduct] = useState('');
  const [whatHappened, setWhatHappened] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filed, setFiled] = useState<ReportComplaintResult | null>(null);

  const ready = customerNo.trim() !== '' && whatHappened.trim() !== '';

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    const meta = {
      customer_no: customerNo.trim().toUpperCase(),
      has_product: product.trim() !== '',
      photo_count: photos.length,
      surface,
    };
    try {
      const result = await reportQualityComplaint({
        customer_no: customerNo.trim(),
        product: product.trim(),
        what_happened: whatHappened.trim(),
        photos,
      });
      trackEvent('action', 'complaint_report_submit', {
        ...meta,
        issue_no: result.issue_no,
        thread_no: result.thread_no,
        outcome: 'request_sent',
      });
      setFiled(result);
      onFiled?.(result);
    } catch {
      trackEvent('action', 'complaint_report_submit', { ...meta, outcome: 'error' });
      setError('The complaint did not go through. Check the photos and try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={modal.overlay} onClick={onClose}>
      <div
        className={modal.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="report-complaint-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={modal.header}>
          <span id="report-complaint-title" className={modal.headerTitle}>
            Report a quality complaint
          </span>
          <button type="button" className={modal.closeBtn} onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>
        {filed ? (
          <div className={modal.body}>
            <p className={styles.filed} role="status">
              Filed as {filed.thread_no} ({filed.issue_no}). The quality desk has it in Triage and will
              come back to you if they need more.
            </p>
            <div className={styles.formActions}>
              <button type="button" className={styles.primaryBtn} onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <form className={modal.body} onSubmit={submit}>
            <div className={modal.fieldGroup}>
              <label className={modal.label} htmlFor="complaint-customer">
                Customer
              </label>
              <input
                id="complaint-customer"
                className={styles.input}
                list="complaint-customer-options"
                value={customerNo}
                onChange={(e) => setCustomerNo(e.target.value)}
                placeholder="Customer number, e.g. PARTO01"
                required
              />
              <datalist id="complaint-customer-options">
                {customers.map((c) => (
                  <option key={c.customer_no} value={c.customer_no}>
                    {c.name ?? c.customer_no}
                  </option>
                ))}
              </datalist>
            </div>
            <div className={modal.fieldGroup}>
              <label className={modal.label} htmlFor="complaint-product">
                Product
              </label>
              <input
                id="complaint-product"
                className={styles.input}
                value={product}
                onChange={(e) => setProduct(e.target.value)}
                placeholder="Product name or item code (optional)"
              />
            </div>
            <div className={modal.fieldGroup}>
              <label className={modal.label} htmlFor="complaint-what">
                What happened
              </label>
              <textarea
                id="complaint-what"
                className={styles.textarea}
                rows={4}
                value={whatHappened}
                onChange={(e) => setWhatHappened(e.target.value)}
                placeholder="What the customer saw, on which order or lot, and what they want"
                required
              />
            </div>
            <div className={modal.fieldGroup}>
              <label className={modal.label} htmlFor="complaint-photos">
                Photos (optional)
              </label>
              <input
                id="complaint-photos"
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => setPhotos(Array.from(e.target.files ?? []))}
              />
            </div>
            {error && (
              <div className={styles.error} role="alert">
                {error}
              </div>
            )}
            <div className={styles.formActions}>
              <button type="button" className={styles.secondaryBtn} onClick={onClose} disabled={busy}>
                Cancel
              </button>
              <button type="submit" className={styles.primaryBtn} disabled={!ready || busy}>
                {busy ? 'Sending…' : 'Send to quality'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
